import { forwardRef, ReactNode } from 'react'

import { Theme } from '@mui/material'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Typography from '@mui/material/Typography'

interface TransferFormRowProps {
  icon: ReactNode
  label: string
  /** id of the label element, so a row button can be named by it. */
  labelId?: string
  /** Right-hand affordance: chevron, pencil or confirm button. */
  action?: ReactNode
  /** When set the whole row is a button (date / destination rows). */
  onClick?: () => void
  disabled?: boolean
  ariaProps?: Record<string, string | boolean | undefined>
  children: ReactNode
}

/** One divider-separated line of the transfer record form: icon, grey label, value, affordance. */
const TransferFormRow = forwardRef<HTMLElement, TransferFormRowProps>(
  ({ icon, label, labelId, action, onClick, disabled, ariaProps, children }, ref) => {
    const layout = {
      display: 'grid',
      gridTemplateColumns: (theme: Theme) =>
        `${theme.spacing(3)} ${theme.spacing(16)} minmax(0, 1fr) ${theme.spacing(5)}`,
      columnGap: 1.5,
      alignItems: 'center',
      width: '100%',
      textAlign: 'left',
      minHeight: (theme: Theme) => theme.spacing(7.5),
      py: 1,
      borderBottom: 1,
      borderColor: 'divider',
    } as const

    const content = (
      <>
        <Box sx={{ display: 'flex', color: 'text.secondary' }}>{icon}</Box>
        <Typography id={labelId} color="text.secondary">
          {label}
        </Typography>
        <Box sx={{ minWidth: 0 }}>{children}</Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', color: 'text.secondary' }}>
          {action}
        </Box>
      </>
    )

    if (onClick) {
      return (
        <ButtonBase
          ref={ref as React.Ref<HTMLButtonElement>}
          onClick={onClick}
          disabled={disabled}
          sx={layout}
          {...ariaProps}
        >
          {content}
        </ButtonBase>
      )
    }
    return (
      <Box ref={ref} sx={layout}>
        {content}
      </Box>
    )
  }
)

TransferFormRow.displayName = 'TransferFormRow'

export default TransferFormRow
